import { Book, Dashboard } from '@apis/index'
import {
  Alert,
  Button,
  Category,
  GridCard,
  Image,
  Row,
  Table,
  TableActions,
  Typography,
} from '@components'
import { IGridItem } from '@components/gridCard'
import { StyleColumn } from '@components/styles'
import { Constant, searchHandler } from '@utils'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Fragment } from 'react/jsx-runtime'
import BookManagementFilter from './components/filter'
import { Actions, store } from '@store/index'
interface IParams {
  page: number
  limit: number
  search: null | string
}
const AdminBookManagement = () => {
  const navigate = useNavigate()
  const { data: dashboard } = useSelector((state: any) => state.bookDashboard)
  const { data: user } = useSelector((state: any) => state.user)
  const [deleteID, setDeleteID] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false)
  const [visible, setVisible] = useState<boolean>(false)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const {
    items: books,
    totalPages,
    loading: fetching,
  } = useSelector((state: any) => state.books)
  const debounceRef: any = useRef(null)
  const [params, setParams] = useState<IParams>({
    page: 1,
    limit: 10,
    search: null,
  })
  const fetchBook = async (params: IParams) => {
    await Book.getBooks('set', params).catch(() => {})
  }
  const fetchDashboard = async () => {
    const params = {
      type: 'book',
    }
    await Dashboard.getAllDashboardCount('bookDashboard', params)
  }
  useEffect(() => {
    fetchBook(params)
    fetchDashboard()
  }, [])
  const items: IGridItem[] = [
    {
      title: 'Total Books Listed',
      value: dashboard?.totalBook || '0',
      Icon: 'book',
      IconBg: 'blueLight',
    },
    {
      title: 'Pending Book Request',
      value: dashboard?.totalPendingBook || '0',
      Icon: 'book',
      IconBg: 'redLight',
    },
    {
      title: 'Total Book Lend',
      value: dashboard?.totalLendBook || '0',
      Icon: 'book',
      IconBg: 'greenLight',
    },
    {
      title: 'Total Book Returned',
      value: dashboard?.totalReturnedBook || '0',
      Icon: 'book',
      IconBg: 'yellowLight',
    },
  ]
  const columns = [
    {
      title: 'Book Image',
      key: 'image',
      render: (item: any) => (
        <Image
          fit="cover"
          height={160}
          width={120}
          src={
            item?.coverImage
              ? `${process.env.BUCKET_URL}/${item?.coverImage}`
              : Constant.defaultNoImage
          }
        />
      ),
    },
    {
      title: 'Book Name',
      key: 'name',
      render: (item: any) => (
        <Typography content={item?.name} tc="greyText" size={14} weigth={400} />
      ),
    },
    {
      title: 'Author',
      key: 'arthur',
      render: (item: any) => (
        <Typography
          content={item?.author}
          tc="greyText"
          size={14}
          weigth={400}
        />
      ),
    },
    {
      title: 'Description',
      key: 'description',
      render: (item: any) => (
        <Typography
          content={item?.description}
          tc="greyText"
          size={14}
          styleText="display: -webkit-box;-webkit-line-clamp: 7;-webkit-box-orient: vertical;overflow: hidden;"
          weigth={400}
        />
      ),
    },
    {
      title: 'Category',
      key: 'category',
      render: (item: any) => <Category category={item?.categoryData?.name} />,
    },
    {
      title: 'Action',
      key: 'action',
      render: (item: any) => (
        <TableActions
          type="icon"
          onDelete={() => {
            setDeleteID(item?.id)
            setVisible(true)
          }}
          onEdit={() => {
            navigate(`/admin/book-managements/update-book/${item?.id}`, {
              state: item,
            })
          }}
          hasDelete={item.createdByAdmin}
          onView={() => {
            navigate(`/admin/book-managements/view-book/${item?.id}`, {
              state: item,
            })
            store.dispatch(Actions.set('book', item))
          }}
        />
      ),
    },
  ]
  const onAdd = () => {
    navigate('/admin/book-managements/add-book')
  }

  const onClickOk = async () => {
    if (!!deleteID) {
      setDeleteLoading(true)
      await Book.deleteBook(deleteID)
        .then(() => {
          setVisible(false)
          setDeleteID(null)
        })
        .catch(() => {})
      setDeleteLoading(false)
    }
  }
  const onSearch = searchHandler({
    fetchFunction: fetchBook,
    paramKey: 'search',
    debounceTime: 600,
    params,
    setParams,
    debounceRef,
  })
  const onChangeFilter = async (filter: any) => {
    store.dispatch(Actions.reset('books'))
    setIsFetching(true)
    const updatedParams: any = { ...params, page: 1, type: null, adminId: null }
    if (filter?.key === 'Self') {
      updatedParams.type = null
      updatedParams.adminId = user?.id
    }
    if (filter?.key === 'Others') {
      updatedParams.adminId = null
      updatedParams.type = filter.key
    }

    await fetchBook(updatedParams)
      .then(() => {
        setParams(updatedParams)
      })
      .catch(() => {})
      .finally(() => {
        setIsFetching(false)
      })
  }

  return (
    <Fragment>
      <StyleColumn>
        <GridCard items={items} />
        <Table
          columns={columns}
          data={books}
          loading={fetching || isFetching}
          tableTitle="Book List"
          onClickRow={(item) => {
            navigate(`/admin/book-managements/view-book/${item?.id}`, {
              state: item,
            })
            store.dispatch(Actions.set('book', item))
          }}
          filter={{
            hasFilter: true,
            hasSearch: true,
            onSearch: onSearch,
            placeholder: 'Book Name, Category',
            rightComponent: <Button title="Add" onClick={onAdd} />,
            filterComponent: (
              <Row>
                <BookManagementFilter onChangeFilter={onChangeFilter} />
              </Row>
            ),
          }}
          paginationProps={{
            totalPages: totalPages,
            perPage: params.limit,
            activePage: params?.page,
            handleOnClick: async (pageNo) => {
              setIsFetching(true)
              const updateParams = { ...params, page: pageNo }
              setParams(updateParams)
              await fetchBook(updateParams)
                .catch(() => {})
                .finally(() => {
                  setIsFetching(false)
                })
            },
          }}
        />
      </StyleColumn>
      <Alert
        loading={deleteLoading}
        title="Are you sure ?"
        description="After delete you would not recover data."
        visible={visible}
        onOk={onClickOk}
        onCancel={() => {
          setVisible(false)
        }}
      />
    </Fragment>
  )
}

export default AdminBookManagement
